import App from '@/app/App'
import { Pills } from '@/app/Pills'
import { tweenGroup } from '@/app/tweenGroup'
import { Easing, Tween } from '@tweenjs/tween.js'
import { Vector2 } from 'three'
import styles from './Logo.module.css'

type Options = {
	node: HTMLElement
	app: App
}

export class Logo {
	private readonly node: HTMLElement
	private readonly app: App
	private readonly border: HTMLElement
	private readonly inner: HTMLElement
	private readonly pills: Pills
	private readonly tweens: Tween[] = []
	private radius = 0
	private p1 = new Vector2()
	private p2 = new Vector2()

	constructor({ node, app }: Options) {
		this.node = node
		this.app = app
		this.inner = node.querySelector(`.${styles.Inner}`) as HTMLElement
		this.border = node.querySelector(`.${styles.Border}`) as HTMLElement
		this.pills = new Pills({ app: app, color: 'black', num: 1 })
	}

	resize() {
		const { width: wWidth, height: wHeight, left: wLeft, top: wTop } = this.inner.getBoundingClientRect()
		const aspect = wHeight / wWidth
		const { width, height, left, top } = this.border.getBoundingClientRect()

		this.pills.resize({ aspect, width: wWidth, height: wHeight, left: wLeft, top: wTop, line: 4, rotate: 0 })

		let x = left - wLeft
		let y = top - wTop

		let w = width
		let h = height

		w /= wWidth
		h /= wHeight

		x /= wWidth
		y /= wHeight

		if (aspect < 1) {
			this.radius = 0.5 * h * aspect

			x += this.radius
			w -= this.radius * 2

			this.p1.set(x, -0.5 * aspect)
			this.p2.set(x + w, -0.5 * aspect)
		} else {
			this.radius = 0.5 * w

			h -= (this.radius * 2) / aspect
			y += this.radius / aspect

			this.p1.set(0.5, -y * aspect)
			this.p2.set(0.5, -y * aspect - h * aspect)
		}

		this.pills.resizePill({ index: 0, p1: this.p1, p2: this.p2, radius: this.radius })

		this.app.requestRender()
	}

	async show() {
		this.tweens.forEach((tween) => tween.stop())
		this.tweens.length = 0

		await this.pills.show()

		this.tweens.push(
			new Tween({ scale: 0.85 }, tweenGroup)
				.to({ scale: 1 }, 750)
				.easing(Easing.Exponential.Out)
				.onUpdate(({ scale }) => (this.node.style.transform = `scale(${scale})`))
				.onStart(() => (this.node.style.opacity = `1`))
				.start()
		)
	}

	dispose() {
		this.pills.dispose()
	}

	update() {
		const { height: wHeight } = this.app.sizes
		const { top, bottom, height } = this.node.getBoundingClientRect()

		if (bottom < 0 || top > wHeight) {
			this.pills.visible = false
			return
		}

		this.pills.visible = true
		const y = (top + height * 0.5 - wHeight * 0.5) / (wHeight * 0.5)

		this.pills.update(y)
	}

	static render() {
		return /* HTML */ `<div class="${styles.Main}">
			<div class="${styles.Inner}">
				<div class="${styles.Border}"></div>
				<h1 class="${styles.Title}">ZiKD</h1>
			</div>
		</div> `
	}
}
